/**
 * Release API Contract
 *
 * oRPC contract for release management.
 * All endpoints require admin authentication.
 */

import { oc } from "@orpc/contract";
import { z } from "zod";

// ============================================
// Zod Schemas
// ============================================

export const ReleasePlatformSchema = z.enum(["web", "marketing", "mobile"]);
export type ReleasePlatform = z.infer<typeof ReleasePlatformSchema>;

export const BaseReleaseSchema = z.object({
  id: z.string(),
  platform: ReleasePlatformSchema,
  gitSha: z.string(),
  commitMessage: z.string().nullable(),
  author: z.string().nullable(),
  timestamp: z.coerce.date(),
  githubUrl: z.string().nullable(),
  isActive: z.boolean(),
});

export const CloudflareReleaseSchema = BaseReleaseSchema.extend({
  platform: z.enum(["web", "marketing"]),
  versionNumber: z.number(),
});
export type CloudflareRelease = z.infer<typeof CloudflareReleaseSchema>;

export const MobileReleaseSchema = BaseReleaseSchema.extend({
  platform: z.literal("mobile"),
  runtimeVersion: z.string(),
  channel: z.string(),
  isCompatible: z.boolean(),
  compatibilityWarning: z.string().nullable(),
});
export type MobileRelease = z.infer<typeof MobileReleaseSchema>;

export const AnyReleaseSchema = z.union([
  CloudflareReleaseSchema,
  MobileReleaseSchema,
]);
export type AnyRelease = z.infer<typeof AnyReleaseSchema>;

export const ListReleasesRequestSchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).optional().default(20),
});
export type ListReleasesRequest = z.infer<typeof ListReleasesRequestSchema>;

export const ListPlatformReleasesRequestSchema = z.object({
  platform: ReleasePlatformSchema,
  limit: z.coerce.number().int().min(1).max(100).optional().default(20),
});
export type ListPlatformReleasesRequest = z.infer<
  typeof ListPlatformReleasesRequestSchema
>;

export const ReleaseListResponseSchema = z.object({
  releases: z.array(z.any()), // Use any for union type compatibility
  activeVersions: z.object({
    web: z.string().nullable(),
    marketing: z.string().nullable(),
    mobile: z.string().nullable(),
  }),
});
export type ReleaseListResponse = z.infer<typeof ReleaseListResponseSchema>;

export const SetActiveReleaseRequestSchema = z.object({
  platform: ReleasePlatformSchema,
  version: z.string().min(1),
  acknowledgeIncompatibility: z.boolean().optional(),
});
export type SetActiveReleaseRequest = z.infer<
  typeof SetActiveReleaseRequestSchema
>;

export const SetActiveReleaseResponseSchema = z.object({
  active: z.object({
    platform: ReleasePlatformSchema,
    version: z.string(),
  }),
  message: z.string(),
});
export type SetActiveReleaseResponse = z.infer<
  typeof SetActiveReleaseResponseSchema
>;

// ============================================
// oRPC Contract Definition
// ============================================

/**
 * Release management oRPC contract.
 * All endpoints require admin authentication.
 */
export const releaseContract = {
  /**
   * List all releases across all platforms.
   */
  list: oc
    .route({ method: "GET", path: "/releases" })
    .input(ListReleasesRequestSchema)
    .output(ReleaseListResponseSchema),

  /**
   * List releases for a specific platform.
   */
  listPlatform: oc
    .route({ method: "GET", path: "/releases/{platform}" })
    .input(ListPlatformReleasesRequestSchema)
    .output(ReleaseListResponseSchema),

  /**
   * Set the active release for a platform (rollback).
   */
  setActive: oc
    .route({ method: "PUT", path: "/releases/{platform}/active" })
    .input(SetActiveReleaseRequestSchema)
    .output(SetActiveReleaseResponseSchema),
};
